/* eslint-disable max-lines */
import {
    Box,
    Button,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { GridSortDirection } from '@mui/x-data-grid';
import { GridSortModel } from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CobranderSelector } from 'app/components/form-controls/cobrander-selector';
import { DatasourceSelector } from 'app/components/form-controls/datasource-selector';
import { TupleFilterInput } from 'app/components/form-controls/tuple-filter-input';
import { StorePicker } from 'app/components/store-picker';
import { GeneralConstants } from 'app/constants';
import { useMLTupleSearch } from 'app/hooks/use-ml-tuple-search';
import { MidTuple, Store } from 'app/model';
import { MlSampleMetadata } from 'app/model/machine-learning/ml-sample-metadata';
import { MidFilter } from 'app/model/mid-filter';
import { messagingService } from 'app/services';
import { mlSampleService } from 'app/services/machine-learning/ml-sample.service';
import { MLTrainingFilter, FilterVersion, MLTrainingFilterType } from 'features/tuples/ml-training-filter';
import { MlTupleResultsTable } from 'features/tuples/ml-tuple-results-table';
import { addFilterField, removeFilterField, selectMidFilters, updateFilterField } from 'features/tuples/tuple.slice';

const setupFixedFilters = (): MidFilter[] => {
    const keys: Array<keyof MidTuple> = ['mid', 'city', 'merchdesc', 'state', 'mcc', 'zip'];

    return keys.map((field) => ({
        field,
        value: '',
        negate: false,
    }));
};

export const MlTraining: React.FC = () => {
    const [samples, setSamples] = useState<MlSampleMetadata[]>([]);
    const [selectedSample, setSelectedSample] = useState<MlSampleMetadata | null>(
        GeneralConstants.COMPONENT_VALUE_NULL,
    );

    const [cobrander, setCobrander] = useState('');
    const [correction, setCorrection] = React.useState(false);
    const [storeField, setStoreField] = React.useState(false);
    const [store, setStore] = React.useState<Store | undefined>();
    const [tupleTable, setTupleTable] = React.useState({
        sortModel: [] as GridSortModel,
        totalTrx: 0,
        totalSpend: 0,
    });
    const [fixedFilters, setFixedFilters] = React.useState(setupFixedFilters());
    const [sampleName, setSampleName] = useState('');
    const [bucketCount, setBucketCount] = useState('');
    const [bucketSize, setBucketSize] = useState('');
    const [loading, setLoading] = useState<boolean>(true);
    const filterData = useSelector(selectMidFilters);
    const [mlTrainingFilter, setMlTrainingFilter] = React.useState<MidFilter>();
    const [mlTrainingFilterVersion, setMlTrainingFilterVersion] = React.useState<FilterVersion | undefined>('basic');
    const [mlTrainingIsNegative, setMlTrainingIsNegative] = React.useState<boolean>(false);
    const handleChangeMlFilter = useCallback<MLTrainingFilterType['onChange']>(
        (newFilter, newFilterVersion, isNegative) => {
            setMlTrainingFilter(newFilter);
            setMlTrainingFilterVersion(newFilterVersion);
            setMlTrainingIsNegative(isNegative || false);
        },
        [],
    );
    const [isTagStore, setIsTagStore] = useState(false);
    const dispatch = useDispatch();

    const { tuples, findTuples, loadMore, totalLabel } = useMLTupleSearch(
        cobrander,
        correction,
        storeField,
        store?.id,
        mlTrainingIsNegative,
        isTagStore,
    );

    const onSort = React.useCallback(
        (field: string | undefined, sort: GridSortDirection) => {
            const model = field ? [{ field, sort }] : [];
            setTupleTable({
                ...tupleTable,
                sortModel: model,
            });
            findTuples(fixedFilters.concat(filterData.filters), model, selectedSample?.id);
        },
        [filterData.filters, findTuples, fixedFilters, tupleTable, selectedSample?.id],
    );

    const handleCreateSample = (): void => {
        mlSampleService
            .createSample({
                name: sampleName,
                bucketCount: parseInt(bucketCount, 10),
                bucketSize: parseInt(bucketSize, 10),
                correction,
                storeId: store?.id,
                cobrander,
                filters: [...fixedFilters, ...filterData.filters, ...(mlTrainingFilter ? [mlTrainingFilter] : [])],
            })
            .subscribe(() => {
                messagingService.showSuccess('Sample creation has been scheduled.');
                setSampleName('');
                setBucketCount('');
                setBucketSize('');
                setLoading(true);
                mlSampleService.listAll().subscribe({
                    next: (data) => {
                        setSamples(data);
                        setLoading(false);
                    },
                });
            });
    };

    useEffect(() => {
        setLoading(true);
        mlSampleService.listAll().subscribe({
            next: (data) => {
                setSamples(data);
                setLoading(false);
            },
        });
    }, []);

    return (
        <Box display='flex' className='m-2' flexDirection='column'>
            <Typography variant='h4'>ML Model Training</Typography>
            <Box display='flex' sx={{ mt: 2, mb: 5 }} alignItems='center' gap={2}>
                <DatasourceSelector
                    samples={samples}
                    loading={loading}
                    value={selectedSample}
                    onChange={(event, newValue) =>
                        setSelectedSample(
                            (newValue as MlSampleMetadata | undefined) ?? GeneralConstants.COMPONENT_VALUE_NULL,
                        )
                    }
                    sx={{ minWidth: '15em' }}
                />
                <Box flexGrow={1}>
                    <StorePicker size='small' value={store} onChange={(event, newStore) => setStore(newStore)} />
                </Box>
                <CobranderSelector
                    value={cobrander}
                    sx={{ minWidth: '15em' }}
                    size='small'
                    variant='outlined'
                    className='ml-1'
                    onChange={(evt) => setCobrander(evt.target.value ?? '')}
                />
            </Box>
            <Typography variant='h6' sx={{ mb: 1.5 }}>
                Default Filters
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack sx={{ mb: 2 }}>
                <Box display='flex' className='my-2' alignItems='center'>
                    <FormControlLabel
                        control={
                            <Switch checked={correction} onChange={(event) => setCorrection(event.target.checked)} />
                        }
                        label={correction ? 'Show Mapped' : 'Show Unmapped'}
                    />
                    <FormControl>
                        <RadioGroup
                            row
                            value={isTagStore ? 'tagStoreId' : storeField ? 'mlStoreId' : 'storeId'}
                            onChange={(event) => {
                                const value = event.target.value;

                                switch (value) {
                                    case 'storeId': {
                                        setStoreField(false);
                                        setIsTagStore(false);

                                        break;
                                    }
                                    case 'mlStoreId': {
                                        setStoreField(true);
                                        setIsTagStore(false);

                                        break;
                                    }
                                    case 'tagStoreId': {
                                        setStoreField(true);
                                        setIsTagStore(true);

                                        break;
                                    }
                                    // No default
                                }
                            }}
                        >
                            <FormControlLabel value='storeId' control={<Radio />} label='Store Id' />
                            <FormControlLabel value='mlStoreId' control={<Radio />} label='ML Store Id' />
                            <FormControlLabel value='tagStoreId' control={<Radio />} label='Tag Store Id' />
                        </RadioGroup>
                    </FormControl>
                </Box>
            </Stack>
            <Grid container spacing={1}>
                {fixedFilters.map((data, key) => {
                    return (
                        <TupleFilterInput
                            key={data.field}
                            value={data}
                            fixed
                            onChange={(change) => {
                                setFixedFilters((current) => {
                                    const clone = [...current];
                                    clone[key] = change;

                                    return clone;
                                });
                            }}
                        />
                    );
                })}
            </Grid>
            <Divider sx={{ my: 4 }} />
            <Grid container spacing={1}>
                {filterData.filters.map((filter, index) => (
                    <TupleFilterInput
                        key={index}
                        value={filter}
                        onChange={(change) => {
                            dispatch(
                                updateFilterField({
                                    field: 'field',
                                    key: index,
                                    value: change.field,
                                }),
                            );
                            dispatch(
                                updateFilterField({
                                    field: 'value',
                                    key: index,
                                    value: change.value,
                                }),
                            );
                            dispatch(
                                updateFilterField({
                                    field: 'negate',
                                    key: index,
                                    value: change.negate,
                                }),
                            );
                        }}
                        onDelete={() => dispatch(removeFilterField(index))}
                    />
                ))}
            </Grid>
            <Box display='flex' sx={{ mt: 1, mb: 2 }}>
                <Box sx={{ width: '50%' }}>
                    <Button
                        onClick={() => dispatch(addFilterField())}
                        variant='outlined'
                        sx={{ textTransform: 'none' }}
                    >
                        Add Additional Filter
                    </Button>
                </Box>
                <Box sx={{ width: '50%' }}>
                    <Typography variant='h6'>Sampling</Typography>
                </Box>
            </Box>
            <Box display='flex' alignItems='center' sx={{ mb: 3 }}>
                <Box sx={{ width: '50%' }} display='flex' alignItems='center' gap={2}>
                    <Button
                        variant='contained'
                        color='secondary'
                        size='large'
                        sx={{ width: '390px' }}
                        onClick={() =>
                            findTuples(
                                fixedFilters.concat(filterData.filters, mlTrainingFilter ? [mlTrainingFilter] : []),
                                tupleTable.sortModel,
                                selectedSample?.id,
                            )
                        }
                    >
                        Compute
                    </Button>
                    <Typography>{totalLabel} matches</Typography>
                </Box>

                <Box sx={{ width: '50%' }} display='flex' alignItems='center' gap={2}>
                    <TextField
                        name='name'
                        label='Name'
                        value={sampleName}
                        onChange={(event) => setSampleName(event.target.value)}
                        size='small'
                        variant='outlined'
                        sx={{ width: '150px' }}
                    />
                    <TextField
                        name='bucketCount'
                        label='Bucket Count'
                        type='number'
                        value={bucketCount}
                        onChange={(event) => setBucketCount(event.target.value)}
                        size='small'
                        variant='outlined'
                        sx={{ width: '150px' }}
                    />
                    <TextField
                        name='bucketSize'
                        label='Bucket Size'
                        type='number'
                        value={bucketSize}
                        onChange={(event) => setBucketSize(event.target.value)}
                        size='small'
                        variant='outlined'
                        sx={{ width: '150px' }}
                    />
                    <Button
                        variant='outlined'
                        onClick={handleCreateSample}
                        disabled={!sampleName || !bucketCount || !bucketSize}
                    >
                        Create Sample
                    </Button>
                </Box>
            </Box>
            <Divider sx={{ mb: 5 }} />
            <Stack direction='row' justifyContent='space-between' alignItems='flex-start' marginBottom={2.5}>
                <Typography variant='h5'>Matches</Typography>
                <MLTrainingFilter onChange={handleChangeMlFilter} isTagStore={isTagStore} />
            </Stack>
            <MlTupleResultsTable
                {...tupleTable}
                tuples={tuples}
                onSort={onSort}
                onLoadMore={loadMore}
                storeBrandVisibility={true}
                mlVisibility
                tagHighlightsVisibility
                checkboxSelection
                primaryStore={store}
                mlTrainingFilterVersion={mlTrainingFilterVersion}
            />
        </Box>
    );
};
